import {
  DocumentSnapshot,
  QueryConstraint,
  QuerySnapshot,
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import {
  IDynamicPaper,
  IDynamicPaperBase,
  IExamSimulationStateSnapshot,
  IExamSimulationStateSnapshotBase,
  IExamSimulationStateSnapshotRaw,
  IDynamicPaperFulfilled,
  IDynamicPaperFulfilledWithLessons,
  IDynamicPaperRaw,
  IDynamicPaperTopicLesson,
  IDynamicPapersHistoryItem,
  IDynamicPapersHistoryItemRaw,
  IGenericError,
  ILessonVariantOpenText,
  THistoryMessage,
  THistoryMessageDoc,
  THistoryMessageOpenText,
  MessageRole,
  IExamGeneralFeedbackBase,
  IExamGeneralFeedbackRaw,
  IExamGeneralFeedback,
  IPostExamMessageBase,
  IPostExamMessage,
  IPostExamMessageRaw,
  IExamGeneralFeedbackFulfilled,
  DpQuestionMode,
  IDpLesson,
} from '../types'
import {
  getDynamicPaperTopicsByIds,
  getDynamicPaperTopicsWithLessonsByIds,
  getDynamicPapersLessonVariantsById,
} from './dynamic-paper-topics'
import { enrichDpLessonsWithMarksStats, getDpLessonsByIds } from './dp-lessons'

const dynamicPapersCollectionName = 'dynamicPapers'
const dynamicPapersHistorySubCollectionName = 'history'
const dynamicPapersMessagesSubCollectionName = 'messages'
const dynamicPapersExamSimulationSubCollectionName = 'examSimulationState'
const dpGeneralFeedbackSessionSubCollectionName = 'generalFeedbackSession'
const dpGeneralFeedbackSessionMessagesSubCollectionName = 'messages'

const dynamicPapersCollectionRef = collection(
  firebaseDb,
  dynamicPapersCollectionName,
)

const dynamicPapersDocRef = (id: string) =>
  doc(firebaseDb, dynamicPapersCollectionName, id)

const historySubCollectionRef = (paperId: string) =>
  collection(
    firebaseDb,
    dynamicPapersCollectionName,
    paperId,
    dynamicPapersHistorySubCollectionName,
  )

const messagesSubCollectionRef = (paperId: string, historyId: string) =>
  collection(
    firebaseDb,
    dynamicPapersCollectionName,
    paperId,
    dynamicPapersHistorySubCollectionName,
    historyId,
    dynamicPapersMessagesSubCollectionName,
  )

const historyMessagesSubCollectionRef = (
  paperId: string,
  historyItemId: string,
) =>
  collection(
    firebaseDb,
    dynamicPapersCollectionName,
    paperId,
    dynamicPapersHistorySubCollectionName,
    historyItemId,
    dynamicPapersMessagesSubCollectionName,
  )

const examSimulationSubCollectionRef = (paperId: string) =>
  collection(
    firebaseDb,
    dynamicPapersCollectionName,
    paperId,
    dynamicPapersExamSimulationSubCollectionName,
  )

const generalFeedbackSessionSubCollectionRef = (paperId: string) =>
  collection(
    firebaseDb,
    dynamicPapersCollectionName,
    paperId,
    dpGeneralFeedbackSessionSubCollectionName,
  )

// Session docs are keyed per user (`${userId}-${paperId}`); callers without a
// user keep the legacy paper-id key so pre-rekey data stays readable.
export const dpGeneralFeedbackSessionDocId = (
  paperId: string,
  userId?: string,
) => (userId ? `${userId}-${paperId}` : paperId)

const generalFeedbackSessionMessagesSubCollectionRef = (
  paperId: string,
  sessionDocId: string = paperId,
) =>
  collection(
    firebaseDb,
    dynamicPapersCollectionName,
    paperId,
    dpGeneralFeedbackSessionSubCollectionName,
    sessionDocId,
    dpGeneralFeedbackSessionMessagesSubCollectionName,
  )

export const getDynamicPaperById = async (
  id: string,
): Promise<IDynamicPaper | undefined> => {
  try {
    const docRef = dynamicPapersDocRef(id)
    const docSnap = (await getDoc(
      docRef,
    )) as unknown as DocumentSnapshot<IDynamicPaperRaw>

    const dacData = docSnap.data()

    if (!dacData) {
      return undefined
    }

    const doc: IDynamicPaper = {
      id: docSnap.id,
      ...dacData,
    }

    return doc
  } catch (error: unknown) {
    console.error(`error while tying to get dynamicPaper by id: ${id} :>> `, {
      message: (error as IGenericError)?.message,
      error,
    })
  }
}

export async function getDynamicPapers(
  queryConstraints: QueryConstraint[] = [],
  lastDocumentSnapshot: DocumentSnapshot | null = null,
  withEarnedMarks: boolean = false,
  userId?: string,
): Promise<{
  dynamicPapers: IDynamicPaper[]
  lastDoc: DocumentSnapshot | null
}> {
  try {
    let q = query(dynamicPapersCollectionRef, ...queryConstraints)

    // If we have a last document, start after it for pagination
    if (lastDocumentSnapshot) {
      // NOTE: to use correct pagination with startAfter cursor, queryConstraints MUST include orderBy constraints
      q = query(q, startAfter(lastDocumentSnapshot))
    }

    const querySnapshot = (await getDocs(q)) as QuerySnapshot<IDynamicPaperRaw>

    if (querySnapshot.empty) {
      return { dynamicPapers: [], lastDoc: null }
    }

    let dynamicPapers: IDynamicPaper[] = []

    querySnapshot.forEach((doc) => {
      dynamicPapers.push({
        id: doc.id,
        ...doc.data(),
        _doc: doc,
      })
    })

    if (withEarnedMarks) {
      // Earned marks are per-user (10 Sep rekey). Callers pass userId;
      // without it keep the legacy whole-collection sum until Pavlo decides
      // migrate-vs-leave for orphaned pre-rekey docs (11 Sep hotfix).
      dynamicPapers = await Promise.all(
        dynamicPapers.map(async (paper) => {
          const earnedMarks = await getDynamicPaperTotalEarnedMarksById(
            paper.id,
            userId,
          )

          return { ...paper, earnedMarks }
        }),
      )
    }

    const lastDoc: DocumentSnapshot | null =
      querySnapshot.docs[querySnapshot.docs.length - 1] || null

    return { dynamicPapers, lastDoc }
  } catch (error: unknown) {
    console.error(`error while trying to get dynamicPapers :>> `, {
      message: (error as IGenericError)?.message,
      error,
      queryConstraints,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPapers`,
    )
  }
}

export async function createDynamicPaper(
  createObj: IDynamicPaperBase,
  examSimulationState?: IExamSimulationStateSnapshotBase,
): Promise<string> {
  try {
    const collectionRef = collection(
      firebaseDb,
      `${dynamicPapersCollectionName}`,
    )

    const doc = await addDoc(collectionRef, {
      ...createObj,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    if (examSimulationState) {
      await createDPExamSimulationState(doc.id, examSimulationState)
    }

    return doc.id
  } catch (error: unknown) {
    console.error(`error while trying to create dynamicPaper :>> `, {
      message: (error as IGenericError)?.message,
      error,
      createObj,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to create dynamicPaper`,
    )
  }
}

export async function deleteDynamicPaper(id: string): Promise<void> {
  try {
    const paperDocRef = dynamicPapersDocRef(id)

    // delete history subCollection & its messages
    await deleteDPHistorySubCollection(id)

    // delete general feedback subCollection & its messages
    await deleteDPGeneralFeedbackSubCollection(id)

    // delete exam simulation state
    const examSnapshot = await getDocs(examSimulationSubCollectionRef(id)) // same id as paper id
    for (const docItem of examSnapshot.docs) {
      await deleteDoc(docItem.ref)
    }

    // Finally, delete the paper document
    await deleteDoc(paperDocRef)
  } catch (error: unknown) {
    console.error(`error while trying to delete dynamicPaper :>> `, {
      message: (error as IGenericError)?.message,
      error,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to delete dynamicPaper`,
    )
  }
}

export async function getDynamicPaperHistorySubCollection(
  id: string,
): Promise<IDynamicPapersHistoryItem[]> {
  try {
    const paperDocRef = historySubCollectionRef(id)

    const snapshot = (await getDocs(
      paperDocRef,
    )) as QuerySnapshot<IDynamicPapersHistoryItemRaw>

    const historyItems: IDynamicPapersHistoryItem[] = []

    for (const element of snapshot.docs) {
      const data = element.data()
      historyItems.push({
        ...data,
        id: element.id,
      })
    }

    return historyItems
  } catch (error: unknown) {
    console.error(
      `error while trying to get dynamicPaper history sub-collection :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPaper history sub-collection`,
    )
  }
}

export async function deleteDPHistorySubCollection(
  paperId: string,
): Promise<void> {
  const historySnapshot = await getDocs(historySubCollectionRef(paperId))
  for (const historyDoc of historySnapshot.docs) {
    await deleteDPHistoryMessages(paperId, historyDoc.id)
    await deleteDoc(historyDoc.ref)
  }
}

export async function deleteDPHistoryMessages(
  paperId: string,
  historyId: string,
): Promise<void> {
  const messagesRef = messagesSubCollectionRef(paperId, historyId)

  // delete messages in batches
  const batchSize = 250
  let hasMoreMessages = true

  while (hasMoreMessages) {
    const messagesSnapshot = await getDocs(query(messagesRef, limit(batchSize)))

    if (messagesSnapshot.empty) {
      hasMoreMessages = false
      break
    }

    const batch = writeBatch(firebaseDb)
    messagesSnapshot.forEach((msgDoc) => batch.delete(msgDoc.ref))
    await batch.commit()

    if (messagesSnapshot.size < batchSize) {
      hasMoreMessages = false
    }
  }
}

export async function updateDynamicPaperById(
  id: string,
  updateObj: Partial<IDynamicPaperRaw>,
): Promise<void> {
  try {
    const docRef = dynamicPapersDocRef(id)

    await updateDoc(docRef, {
      ...updateObj,
      updatedAt: serverTimestamp(),
    })
  } catch (error: unknown) {
    console.error(
      `error while tying to update dynamicPaper by id: ${id} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while tying to update dynamicPaper by id`,
    )
  }
}

export async function getDynamicPaperFulfilledById(
  id: string,
): Promise<IDynamicPaperFulfilled | undefined> {
  try {
    const docRef = dynamicPapersDocRef(id)
    const docSnap = (await getDoc(
      docRef,
    )) as unknown as DocumentSnapshot<IDynamicPaperRaw>

    const dacData = docSnap.data()

    if (!dacData) {
      return undefined
    }

    const doc: IDynamicPaperFulfilled = {
      id: docSnap.id,
      ...dacData,
      topics: (await getDynamicPaperTopicsByIds(dacData.topicIds)) || [],
    }

    return doc
  } catch (error: unknown) {
    console.error(
      `error while tying to get dynamicPaper fulfilled by id: ${id} :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
      },
    )
  }
}

export async function getDynamicPaperFulfilledWithLessonsById(
  paperId: string,
  withVariants: boolean = false,
  userId?: string,
): Promise<IDynamicPaperFulfilledWithLessons | undefined> {
  try {
    const docRef = dynamicPapersDocRef(paperId)
    const docSnap = (await getDoc(
      docRef,
    )) as unknown as DocumentSnapshot<IDynamicPaperRaw>

    const dacData = docSnap.data()

    if (!dacData) {
      return undefined
    }

    let lessons: IDynamicPaperTopicLesson[] = []

    // handle whole question case
    if (dacData.questionMode === DpQuestionMode.WholeQuestion) {
      lessons = await getDpLessonsByIds(
        dacData.lessonIds,
        true, // sync order in whole question flow
      )

      lessons = await enrichDpLessonsWithMarksStats(
        paperId,
        lessons as IDpLesson[],
        withVariants,
        userId,
      )
    } else {
      // handle question parts case
      const topics =
        (await getDynamicPaperTopicsWithLessonsByIds(dacData.topicIds)) || []

      lessons = topics.flatMap((topic) =>
        topic.lessons.map((l) => ({ ...l, topicDoc: topic })),
      )

      // Single-question practice papers (Exam Questions "Answer with Axi")
      // carry the chosen question id in lessonIds; keep only those rows.
      if (dacData.lessonIds?.length) {
        const wanted = new Set(dacData.lessonIds)
        const narrowed = lessons.filter((l) => wanted.has(l.id))

        if (narrowed.length) {
          lessons = narrowed
        }
      }

      lessons = await enrichDynamicPaperLessonsWithMarksStats(
        paperId,
        lessons,
        withVariants,
        userId,
      )
    }

    const doc: IDynamicPaperFulfilledWithLessons = {
      id: docSnap.id,
      ...dacData,
      lessons,
    }

    return doc
  } catch (error: unknown) {
    console.error(
      `error while tying to get dynamicPaper fulfilled by id: ${paperId} :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
      },
    )
  }
}

export async function getDynamicPaperHistoryById(
  paperId: string,
  historyItemId: string, // history id schema: `${topicId}-${lessonId}`
): Promise<IDynamicPapersHistoryItem | undefined> {
  try {
    const docRef = doc(
      firebaseDb,
      dynamicPapersCollectionName,
      paperId,
      dynamicPapersHistorySubCollectionName,
      historyItemId,
    )

    const docSnap = (await getDoc(
      docRef,
    )) as unknown as DocumentSnapshot<IDynamicPapersHistoryItemRaw>

    if (!docSnap.exists()) {
      return undefined
    }

    const document: IDynamicPapersHistoryItem = {
      id: docSnap.id,
      ...docSnap.data(),
      messages:
        (await getDynamicPaperHistoryMessagesById(paperId, historyItemId)) ||
        [],
    }

    return document
  } catch (error: unknown) {
    console.error(`error while tying to get dynamicPaper history :>> `, {
      message: (error as IGenericError)?.message,
      error,
      paperId,
      historyItemId,
    })
  }
}

export async function getDynamicPaperHistoryMessagesById(
  paperId: string,
  historyItemId: string,
): Promise<THistoryMessageDoc[]> {
  try {
    const collectionRef = historyMessagesSubCollectionRef(
      paperId,
      historyItemId,
    )

    const querySnapshot = (await getDocs(
      query(collectionRef, orderBy('createdAt', 'asc')),
    )) as unknown as QuerySnapshot<THistoryMessageOpenText>

    const messages: THistoryMessageDoc[] = []

    querySnapshot.docs.forEach((doc) => {
      const message: THistoryMessageDoc = {
        id: doc.id,
        ...doc.data(),
      }
      messages.push(message)
    })

    return messages
  } catch (error: unknown) {
    console.error(
      `error while tying to get dynamicPaper history messages :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        paperId,
        historyItemId,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPaper history messages`,
    )
  }
}

export async function saveDynamicPaperMessageToHistory(
  paperId: string,
  historyItemId: string, // history id schema: `${topicId}-${lessonId}`
  message: THistoryMessage,
) {
  try {
    const collectionRef = historyMessagesSubCollectionRef(
      paperId,
      historyItemId,
    )

    await addDoc(collectionRef, {
      ...message,
      createdAt: serverTimestamp(),
    })
  } catch (error: unknown) {
    console.error(
      `error while trying to save dynamicPaper message history :>> `,
      (error as IGenericError)?.message || error,
      error,
      paperId,
      historyItemId,
      message,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to save dynamicPaper message history`,
    )
  }
}

export async function removeDynamicPaperMessageFromHistory(
  paperId: string,
  historyItemId: string, // history id schema: `${topicId}-${lessonId}`
  messageId: string,
) {
  try {
    const collectionRef = historyMessagesSubCollectionRef(
      paperId,
      historyItemId,
    )

    await deleteDoc(doc(collectionRef, messageId))
  } catch (error: unknown) {
    console.error(
      `error while trying to remove dynamicPaper message history :>> `,
      (error as IGenericError)?.message || error,
      error,
      paperId,
      historyItemId,
      messageId,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to remove dynamicPaper message history`,
    )
  }
}

export async function removeDynamicPaperMessagesByRolesFromHistory(
  paperId: string,
  historyItemId: string, // history id schema: `${topicId}-${lessonId}`
  rolesToDelete: MessageRole[],
): Promise<number> {
  try {
    const collectionRef = historyMessagesSubCollectionRef(
      paperId,
      historyItemId,
    )

    const q = query(collectionRef, where('role', 'in', rolesToDelete))

    const querySnapshot = await getDocs(q)

    const batch = writeBatch(firebaseDb)
    let deletedCount = 0

    if (querySnapshot.empty) {
      console.debug(
        `removeDynamicPaperMessagesByRolesFromHistory :>> No documents with roles ${rolesToDelete.join(
          ', ',
        )} found to delete.`,
      )
      return 0
    }

    querySnapshot.forEach((document) => {
      batch.delete(document.ref)
      deletedCount++
    })

    await batch.commit()

    console.debug(
      `removeDynamicPaperMessagesByRolesFromHistory :>> Successfully deleted ${deletedCount} messages from dynamicPaper message history.`,
    )
    return deletedCount
  } catch (error: unknown) {
    console.error(
      `error while trying to remove dynamicPaper messages by roles from history :>> `,
      (error as IGenericError)?.message || error,
      error,
      paperId,
      historyItemId,
      rolesToDelete,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to remove dynamicPaper messages by roles from history`,
    )
  }
}

export async function updateDynamicPaperHistory(
  paperId: string,
  documentId: string,
  updateObj: Partial<IDynamicPapersHistoryItemRaw>,
) {
  try {
    const collectionRef = historySubCollectionRef(paperId)

    const documentRef = doc(collectionRef, documentId)

    await setDoc(
      documentRef,
      { ...updateObj, updatedAt: serverTimestamp() },
      { merge: true },
    )
  } catch (error: unknown) {
    console.error(`error while trying to update lesson history item. :>> `, {
      message: (error as IGenericError)?.message,
      error,
      paperId,
      documentId,
      updateObj,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to update lesson history item`,
    )
  }
}

// Per-user history docs for one paper (10 Sep rekey): the results page
// totals only the signed-in student's marking history.
export async function getDynamicPaperUserHistoryByUserId(
  paperId: string,
  userId: string,
): Promise<IDynamicPapersHistoryItem[]> {
  try {
    const snapshot = (await getDocs(
      query(historySubCollectionRef(paperId), where('userId', '==', userId)),
    )) as QuerySnapshot<IDynamicPapersHistoryItemRaw>

    return snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    })) as IDynamicPapersHistoryItem[]
  } catch (error: unknown) {
    console.error(
      'error while trying to get dynamic paper user history :>> ',
      {
        message: (error as IGenericError)?.message,
        error,
        paperId,
        userId,
      },
    )

    return []
  }
}

export async function getDynamicPaperTotalEarnedMarksById(
  paperId: string,
  userId?: string,
): Promise<number> {
  try {
    let totalEarnedMarks = 0

    const historyCollectionRef = historySubCollectionRef(paperId)

    // Per-user history (10 Sep rekey): when userId is given, sum only that
    // student's docs. Without it, keep the legacy whole-collection sum so
    // orphaned pre-rekey docs stay readable until Pavlo decides
    // migrate-vs-leave (11 Sep hotfix restores the fallback).
    const snapshot = (await getDocs(
      userId
        ? query(historyCollectionRef, where('userId', '==', userId))
        : historyCollectionRef,
    )) as QuerySnapshot<IDynamicPapersHistoryItemRaw>

    if (snapshot.empty) {
      return totalEarnedMarks
    }

    snapshot.docs.forEach((doc) => {
      const earnedMarks = doc.data().earnedMarks || 0

      totalEarnedMarks += earnedMarks
    })

    return totalEarnedMarks
  } catch (error: unknown) {
    console.error(
      `error while trying to get dynamic paper history totalEarnedMarks :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        paperId,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamic paper totalEarnedMarks`,
    )
  }
}

export async function enrichDynamicPaperLessonsWithMarksStats(
  paperId: string,
  lessons: IDynamicPaperTopicLesson[],
  withVariants: boolean = false,
  userId?: string,
): Promise<IDynamicPaperTopicLesson[]> {
  try {
    const updatedLessons: IDynamicPaperTopicLesson[] = await Promise.all(
      lessons.map(async (lesson) => {
        const variants = await getDynamicPapersLessonVariantsById(
          lesson.topicId,
          lesson.id,
        )

        const totalMarks: number = variants.reduce((acc: number, next) => {
          const marks = Number((next as ILessonVariantOpenText)?.marks)
          return acc + (Number.isFinite(marks) ? marks : 0)
        }, 0)

        const history: IDynamicPapersHistoryItem | undefined =
          await getDynamicPaperHistoryById(
            paperId,
            userId
              ? `${userId}-${lesson.topicId}-${lesson.id}`
              : `${lesson.topicId}-${lesson.id}`,
          )

        const earnedMarks = history?.earnedMarks || 0

        const updated: IDynamicPaperTopicLesson = {
          ...lesson,
          totalMarks,
          earnedMarks,
          hasMarking: !!history?.lastMarksUpdate,
          completed: earnedMarks > 0 && earnedMarks === totalMarks,
          wasAttempted:
            history?.messages?.some(
              (message) => message.role === MessageRole.User,
            ) ?? false,
          ...(withVariants ? { variants } : {}),
        }

        return updated
      }),
    )

    return updatedLessons
  } catch (error: unknown) {
    console.error(
      `error while trying to enrich lessons with marks stats :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        paperId,
        lessons,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get lessons marks stats`,
    )
  }
}

// exam simulation doc id is same as paper id (only one document in sub-collection)
export async function getDPExamSimulationState(
  paperId: string,
): Promise<IExamSimulationStateSnapshot | undefined> {
  try {
    const docRef = doc(examSimulationSubCollectionRef(paperId), paperId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return undefined
    }

    const data = docSnap.data() as IExamSimulationStateSnapshotRaw

    const docData: IExamSimulationStateSnapshot = {
      id: docSnap.id,
      ...data,
    }

    return docData
  } catch (error: unknown) {
    console.error(
      `error while tying to get dynamicPaper exam simulation state by id: ${paperId} :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
      },
    )
  }
}

export async function updateDPExamSimulationState(
  paperId: string,
  updateObj: Partial<IExamSimulationStateSnapshotRaw>,
): Promise<void> {
  try {
    const docRef = doc(examSimulationSubCollectionRef(paperId), paperId)

    await updateDoc(docRef, {
      ...updateObj,
      updatedAt: Timestamp.now(),
    })
  } catch (error: unknown) {
    console.error(
      `error while tying to update dynamicPaper exam simulation state by id: ${paperId} :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to update dynamicPaper exam simulation state`,
    )
  }
}

export async function createDPExamSimulationState(
  paperId: string,
  createObj: IExamSimulationStateSnapshotBase,
): Promise<string> {
  try {
    const docRef = doc(examSimulationSubCollectionRef(paperId), paperId)

    await setDoc(docRef, {
      ...createObj,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    })

    return paperId
  } catch (error: unknown) {
    console.error(
      `error while tying to create dynamicPaper exam simulation state by id: ${paperId} :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to create dynamicPaper exam simulation state`,
    )
  }
}

export async function createDpExamGeneralFeedbackSession(
  paperId: string,
  createObj: IExamGeneralFeedbackBase,
  userId?: string,
): Promise<string> {
  try {
    const collectionRef = generalFeedbackSessionSubCollectionRef(paperId)
    const docRef = doc(
      collectionRef,
      dpGeneralFeedbackSessionDocId(paperId, userId),
    )

    await setDoc(docRef, {
      ...createObj,
      ...(userId ? { userId } : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    return docRef.id
  } catch (error: unknown) {
    console.error(
      `error while tying to create dynamicPaper exam general feedback session by id: ${paperId} :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        createObj,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to create dynamicPaper exam general feedback session`,
    )
  }
}

export async function getDpExamGeneralFeedbackSessionById(
  paperId: string,
  userId?: string,
): Promise<IExamGeneralFeedback | undefined> {
  try {
    const collectionRef = generalFeedbackSessionSubCollectionRef(paperId)

    const docSnap = await getDoc(
      doc(collectionRef, dpGeneralFeedbackSessionDocId(paperId, userId)),
    )

    if (!docSnap.exists()) {
      return undefined
    }

    const data = docSnap.data() as IExamGeneralFeedbackRaw

    const docData: IExamGeneralFeedback = {
      id: docSnap.id,
      ...data,
    }

    return docData
  } catch (error: unknown) {
    console.error(
      `error while tying to get dynamicPaper exam general feedback session :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPaper exam general feedback session`,
    )
  }
}

export async function updateDpExamGeneralFeedbackSessionById(
  paperId: string,
  updateObj: Partial<IExamGeneralFeedbackRaw>,
  userId?: string,
): Promise<void> {
  try {
    const collectionRef = generalFeedbackSessionSubCollectionRef(paperId)
    const docRef = doc(
      collectionRef,
      dpGeneralFeedbackSessionDocId(paperId, userId),
    )

    await updateDoc(docRef, {
      ...updateObj,
      updatedAt: serverTimestamp(),
    })
  } catch (error: unknown) {
    console.error(
      `error while tying to update dynamicPaper exam general feedback session by id: ${paperId} :>> `,
      { message: (error as IGenericError)?.message, error, updateObj },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while tying to update dynamicPaper exam general feedback session by id`,
    )
  }
}

export async function getDpExamGeneralFeedbackSessionFulfilledById(
  paperId: string,
  userId?: string,
): Promise<IExamGeneralFeedbackFulfilled | undefined> {
  try {
    const collectionRef = generalFeedbackSessionSubCollectionRef(paperId)
    const sessionDocId = dpGeneralFeedbackSessionDocId(paperId, userId)

    const docSnap = await getDoc(doc(collectionRef, sessionDocId))

    if (!docSnap.exists()) {
      return undefined
    }

    const data = docSnap.data() as IExamGeneralFeedbackRaw

    const messages = await getDpExamGeneralFeedbackMessages(paperId, sessionDocId)

    const docData: IExamGeneralFeedbackFulfilled = {
      id: docSnap.id,
      ...data,
      messages,
    }

    return docData
  } catch (error: unknown) {
    console.error(
      `error while tying to get dynamicPaper exam general feedback session with messages:>> `,
      {
        message: (error as IGenericError)?.message,
        error,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPaper exam general feedback session with messages`,
    )
  }
}

export async function saveDpExamGeneralFeedbackMessage(
  paperId: string,
  sessionDocId: string,
  createObj: IPostExamMessageBase,
  timestamps?: {
    createdAt?: Timestamp
    updatedAt?: Timestamp
  },
): Promise<string> {
  try {
    const collectionRef = generalFeedbackSessionMessagesSubCollectionRef(
      paperId,
      sessionDocId,
    )

    const doc = await addDoc(collectionRef, {
      ...createObj,
      createdAt: timestamps?.createdAt ?? serverTimestamp(),
      updatedAt: timestamps?.updatedAt ?? serverTimestamp(),
    })

    return doc.id
  } catch (error: unknown) {
    console.error(
      `error while tying to save dynamicPaper exam general feedback message by id: ${paperId} :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        createObj,
        timestamps,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to save dynamicPaper exam general feedback message`,
    )
  }
}

export async function updateDpExamGeneralFeedbackMessage(
  paperId: string,
  sessionDocId: string,
  messageId: string,
  updateObj: Partial<IPostExamMessageBase>,
): Promise<void> {
  try {
    const collectionRef = generalFeedbackSessionMessagesSubCollectionRef(
      paperId,
      sessionDocId,
    )

    await updateDoc(doc(collectionRef, messageId), {
      ...updateObj,
      updatedAt: serverTimestamp(),
    })
  } catch (error: unknown) {
    console.error(
      `error while tying to update dynamicPaper exam general feedback message by id: ${messageId} :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        paperId,
        updateObj,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to update dynamicPaper exam general feedback message`,
    )
  }
}

export async function removeDpExamGeneralFeedbackMessages(
  paperId: string,
  sessionDocId: string,
  queryConstraints: QueryConstraint[] = [],
) {
  try {
    const collectionRef = generalFeedbackSessionMessagesSubCollectionRef(
      paperId,
      sessionDocId,
    )

    const q = query(collectionRef, ...queryConstraints)

    const querySnapshot = await getDocs(q)

    const batch = writeBatch(firebaseDb)
    let deletedCount = 0

    if (querySnapshot.empty) {
      console.debug(
        'removeDpExamGeneralFeedbackMessages :>> No documents found in General Feedback to delete based on constraints.',
      )
      return 0
    }

    querySnapshot.forEach((document) => {
      batch.delete(document.ref)
      deletedCount++
    })

    await batch.commit()

    console.debug(
      `removeDpExamGeneralFeedbackMessages :>> Successfully deleted ${deletedCount} General Feedback messages for paperId: ${paperId}.`,
    )
    return deletedCount
  } catch (error: unknown) {
    console.error(
      `error while tying to remove dynamicPaper exam general feedback messages :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        queryConstraints,
        paperId,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to remove dynamicPaper exam general feedback messages`,
    )
  }
}

export async function getDpExamGeneralFeedbackMessages(
  paperId: string,
  sessionDocId: string = paperId,
): Promise<IPostExamMessage[]> {
  try {
    const collectionRef = generalFeedbackSessionMessagesSubCollectionRef(
      paperId,
      sessionDocId,
    )

    const querySnapshot = (await getDocs(
      query(collectionRef, orderBy('createdAt', 'asc')),
    )) as unknown as QuerySnapshot<IPostExamMessageRaw>

    const messages: IPostExamMessage[] = []

    querySnapshot.docs.forEach((doc) => {
      const message: IPostExamMessage = {
        id: doc.id,
        ...doc.data(),
      }
      messages.push(message)
    })

    return messages
  } catch (error: unknown) {
    console.error(
      `error while tying to get dynamicPaper exam general feedback messages :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        paperId,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPaper exam general feedback messages`,
    )
  }
}

export async function deleteDPGeneralFeedbackSubCollection(
  paperId: string,
): Promise<void> {
  const generalFeedbackSnapshot = await getDocs(
    generalFeedbackSessionSubCollectionRef(paperId),
  )
  for (const generalFeedbackDoc of generalFeedbackSnapshot.docs) {
    await deleteDPGeneralFeedbackMessages(paperId, generalFeedbackDoc.id)
    await deleteDoc(generalFeedbackDoc.ref)
  }
}

export async function deleteDPGeneralFeedbackMessages(
  paperId: string,
  sessionDocId: string = paperId,
  queryConstraints: QueryConstraint[] = [],
): Promise<void> {
  const messagesRef = generalFeedbackSessionMessagesSubCollectionRef(
    paperId,
    sessionDocId,
  )

  // delete messages in batches
  const batchSize = 250
  let hasMoreMessages = true

  while (hasMoreMessages) {
    const messagesSnapshot = await getDocs(
      query(messagesRef, limit(batchSize), ...queryConstraints),
    )

    if (messagesSnapshot.empty) {
      hasMoreMessages = false
      break
    }

    const batch = writeBatch(firebaseDb)
    messagesSnapshot.forEach((msgDoc) => batch.delete(msgDoc.ref))
    await batch.commit()

    if (messagesSnapshot.size < batchSize) {
      hasMoreMessages = false
    }
  }
}

// Function to migrate dynamic papers from anonymous user to authenticated user
export const migrateDynamicPapersFromAnonymous = async (
  anonymousUserId: string,
  userId: string,
) => {
  try {
    // 1. Migrate dynamicPapers collection
    const dynamicPapersQuery = query(
      dynamicPapersCollectionRef,
      where('userId', '==', anonymousUserId),
    )
    const dynamicPapersSnapshot = await getDocs(dynamicPapersQuery)

    if (dynamicPapersSnapshot.empty) {
      console.debug(
        '[Dynamic Papers Migration]: No papers to migrate for this user',
      )
      return
    }

    console.debug(
      `[Dynamic Papers Migration]: found ${dynamicPapersSnapshot.size} papers to migrate for this user`,
    )

    const batch = writeBatch(firebaseDb)
    dynamicPapersSnapshot.docs.forEach((docItem) => {
      batch.update(docItem.ref, {
        userId: userId,
        updatedAt: serverTimestamp(),
      })
    })
    await batch.commit()

    console.debug(
      `[Dynamic Papers Migration]: ${dynamicPapersSnapshot.size} papers successfully migrated.`,
    )
  } catch (error) {
    console.error('[Dynamic Papers Migration]: Error: ', error)
    throw error
  }
}
